import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Film, Upload, Crop, Type, Mic } from "lucide-react";

/**
 * Self-contained panel that owns all AI-video state:
 * slideshow (Tier 2) / Sora 2 (Tier 1) / fal.ai UGC (Tier 3) / buyer upload /
 * auto-remix to 3 aspect ratios / optional Claude-generated caption overlay.
 *
 * Props:
 *  - brief: the current brief object (required for Sora/UGC/remix persistence)
 *  - images: array of /api/static/generated/... image URLs (for slideshow)
 *  - BACKEND: REACT_APP_BACKEND_URL (for resolving static mp4 URLs)
 */
export default function VideoTiersPanel({ brief, images = [], BACKEND, onVideoChange }) {
  const [videoUrl, _setVideoUrl] = useState(null);
  const setVideoUrl = (u) => {
    _setVideoUrl(u);
    if (onVideoChange) onVideoChange(u);
  };
  const [videoTier, setVideoTier] = useState("slideshow");
  const [videoJob, setVideoJob] = useState(null);
  const [remixUrls, setRemixUrls] = useState(null);
  const [soraDuration, setSoraDuration] = useState(4);
  const [falModels, setFalModels] = useState([]);
  const [falModel, setFalModel] = useState("kling-v1-std");
  const [falDuration, setFalDuration] = useState(5);
  const [overlayText, setOverlayText] = useState("");
  const [autoCaption, setAutoCaption] = useState(true);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState("sarah");
  const [voScript, setVoScript] = useState("");
  const [voMix, setVoMix] = useState(false);
  const [busy, setBusy] = useState({
    slide: false, sora: false, fal: false, upload: false, remix: false, vo: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ai/video/models");
        setFalModels(data.fal_models || []);
      } catch {}
      try {
        const { data } = await api.get("/ai/video/voices");
        setVoices(data.voices || []);
        if (data.default) setVoice(data.default);
      } catch {}
    })();
  }, []);

  const pollJob = async (jobId, tier) => {
    const startedAt = Date.now();
    const deadlineMs = (tier === "sora" ? 15 : 6) * 60 * 1000;
    let delay = 4000;
    while (Date.now() - startedAt < deadlineMs) {
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay + 1000, 10000);
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      try {
        const { data } = await api.get(`/ai/video/job/${jobId}`);
        setVideoJob({ job_id: jobId, status: data.status, tier, elapsed_sec: elapsed });
        if (data.status === "completed") return { video_url: data.video_url };
        if (data.status === "failed") throw new Error(data.error || "Render failed");
      } catch (e) {
        if (e?.message && !e?.response) throw e;
      }
    }
    throw new Error("Render timed out — check backend logs");
  };

  const buildSlideshow = async () => {
    if (!brief || images.length === 0) { toast.error("Generate images first"); return; }
    setBusy((b) => ({ ...b, slide: true }));
    try {
      const { data } = await api.post("/ai/video", { brief_id: brief.brief_id, duration_per_scene: 3.5 });
      setVideoUrl(data.video_url);
      setVideoTier("slideshow");
      setRemixUrls(null);
      toast.success("Slideshow ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video build failed");
    } finally {
      setBusy((b) => ({ ...b, slide: false }));
    }
  };

  const buildSora = async () => {
    if (!brief) { toast.error("Generate brief first"); return; }
    setBusy((b) => ({ ...b, sora: true }));
    setVideoJob(null);
    toast.message("Sora 2 queued — usually ready in 60–180s");
    try {
      const { data } = await api.post("/ai/video/sora", {
        brief_id: brief.brief_id, duration: soraDuration,
        orientation: "portrait", model: "sora-2",
      });
      setVideoJob({ job_id: data.job_id, status: "queued", tier: "sora", elapsed_sec: 0 });
      const res = await pollJob(data.job_id, "sora");
      setVideoUrl(res.video_url); setVideoTier("sora");
      setVideoJob(null); setRemixUrls(null);
      toast.success(`Sora 2 video ready (${soraDuration}s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Sora failed");
      setVideoJob(null);
    } finally {
      setBusy((b) => ({ ...b, sora: false }));
    }
  };

  const buildFal = async () => {
    if (!brief) { toast.error("Generate brief first"); return; }
    setBusy((b) => ({ ...b, fal: true }));
    setVideoJob(null);
    toast.message("fal.ai UGC queued — usually ready in 90–300s");
    try {
      const { data } = await api.post("/ai/video/fal-ugc", {
        brief_id: brief.brief_id, duration: falDuration,
        aspect_ratio: "9:16", model: falModel,
      });
      setVideoJob({ job_id: data.job_id, status: "queued", tier: "fal_ugc", elapsed_sec: 0 });
      const res = await pollJob(data.job_id, "fal_ugc");
      setVideoUrl(res.video_url); setVideoTier("fal_ugc");
      setVideoJob(null); setRemixUrls(null);
      toast.success(`UGC video ready (${falDuration}s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "UGC failed");
      setVideoJob(null);
    } finally {
      setBusy((b) => ({ ...b, fal: false }));
    }
  };

  const uploadOwn = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("Max 100 MB"); return; }
    setBusy((b) => ({ ...b, upload: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/ai/video/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoUrl(data.video_url); setVideoTier("upload"); setRemixUrls(null);
      toast.success(`Uploaded · ${(data.size_bytes / (1024 * 1024)).toFixed(1)} MB`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy((b) => ({ ...b, upload: false }));
    }
  };

  const remix = async () => {
    if (!videoUrl) { toast.error("No source video yet"); return; }
    setBusy((b) => ({ ...b, remix: true }));
    try {
      const payload = {
        source_url: videoUrl,
        brief_id: brief?.brief_id,
        overlay_text: overlayText.trim() || undefined,
        auto_caption: !overlayText.trim() && autoCaption ? true : undefined,
      };
      const { data } = await api.post("/ai/video/remix", payload);
      setRemixUrls(data.variants || null);
      const n = Object.keys(data.variants || {}).length;
      if (data.caption_used) toast.success(`${n} variants with caption "${data.caption_used}"`);
      else toast.success(`${n} aspect ratio${n === 1 ? "" : "s"} ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remix failed");
    } finally {
      setBusy((b) => ({ ...b, remix: false }));
    }
  };

  const addVoiceover = async () => {
    if (!videoUrl) { toast.error("No source video yet"); return; }
    const script = voScript.trim();
    if (!script) { toast.error("Enter the script to narrate"); return; }
    if (script.length > 400) { toast.error("Script too long (max 400 chars)"); return; }
    setBusy((b) => ({ ...b, vo: true }));
    try {
      const { data } = await api.post("/ai/video/voiceover", {
        source_url: videoUrl,
        script,
        voice,
        mix_with_original: voMix,
        brief_id: brief?.brief_id,
      });
      // Replace current preview with the voiced version so remix picks it up
      setVideoUrl(data.video_url);
      setRemixUrls(null);
      toast.success(`Voiceover added (${voice})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voiceover failed");
    } finally {
      setBusy((b) => ({ ...b, vo: false }));
    }
  };

  // Per-variant voiceover: stack voice on a single remixed aspect ratio (e.g.
  // reels / feed / square) so the buyer can keep the other crops untouched.
  const addVoiceoverToVariant = async (variantKey) => {
    const src = remixUrls?.[variantKey];
    if (!src) return;
    const script = voScript.trim();
    if (!script) { toast.error("Enter the script in the Tier-4 panel first"); return; }
    if (script.length > 400) { toast.error("Script too long (max 400 chars)"); return; }
    setBusy((b) => ({ ...b, [`vo_${variantKey}`]: true }));
    try {
      const { data } = await api.post("/ai/video/voiceover", {
        source_url: src,
        script,
        voice,
        mix_with_original: voMix,
      });
      setRemixUrls((prev) => ({ ...prev, [variantKey]: data.video_url }));
      toast.success(`Voiceover added to ${variantKey} (${voice})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voiceover failed");
    } finally {
      setBusy((b) => ({ ...b, [`vo_${variantKey}`]: false }));
    }
  };

  return (
    <>
      {/* Slideshow + A/B variants + email digest row is kept in parent. */}

      {/* AI Video Tiers */}
      <div className="pt-3 border-t border-slate-200" data-testid="video-tiers">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">AI Video Tiers (9:16)</div>
          <label className="cursor-pointer text-[11px] font-mono text-blue-700 hover:text-blue-900 flex items-center gap-1" data-testid="upload-video-label">
            <Upload className="w-3 h-3" />
            {busy.upload ? "Uploading…" : "Upload own video"}
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v"
              onChange={uploadOwn}
              disabled={busy.upload}
              data-testid="upload-video-input"
              className="hidden"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {/* Sora */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-800">Tier 1 · Sora 2 cinematic</div>
              <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-700 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5">OpenAI/Emergent</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Len</label>
              <select
                value={soraDuration}
                onChange={(e) => setSoraDuration(parseInt(e.target.value, 10))}
                data-testid="sora-duration"
                className="bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
              >
                <option value={4}>4s</option>
                <option value={8}>8s</option>
                <option value={12}>12s</option>
              </select>
              <button
                onClick={buildSora}
                disabled={busy.sora || !brief}
                data-testid="build-sora-btn"
                className="flex-1 flex items-center justify-center gap-1 bg-slate-900 hover:bg-slate-800 text-white rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
              >
                <Film className="w-3.5 h-3.5" /> {busy.sora ? "Rendering…" : "Generate Sora"}
              </button>
            </div>
            <div className="text-[10px] text-slate-500 font-mono">Polished b-roll · ~$0.10/sec</div>
          </div>

          {/* fal UGC */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-800">Tier 3 · UGC (fal.ai)</div>
              <span className="text-[9px] font-mono uppercase tracking-wider text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">FAL_KEY</span>
            </div>
            <select
              value={falModel}
              onChange={(e) => {
                const m = e.target.value;
                setFalModel(m);
                const cfg = falModels.find((x) => x.key === m);
                if (cfg?.durations?.length) setFalDuration(cfg.durations[0]);
              }}
              data-testid="fal-model"
              className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
            >
              {falModels.length === 0 && <option value="kling-v1-std">Kling v1 · Standard</option>}
              {falModels.map((m) => (
                <option key={m.key} value={m.key}>{m.label} · ${m.rate_usd_per_sec}/s</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Len</label>
              <select
                value={falDuration}
                onChange={(e) => setFalDuration(parseInt(e.target.value, 10))}
                data-testid="fal-duration"
                className="bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
              >
                {(falModels.find((m) => m.key === falModel)?.durations || [5, 10]).map((d) => (
                  <option key={d} value={d}>{d}s</option>
                ))}
              </select>
              <button
                onClick={buildFal}
                disabled={busy.fal || !brief}
                data-testid="build-fal-btn"
                className="flex-1 flex items-center justify-center gap-1 bg-slate-900 hover:bg-slate-800 text-white rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
              >
                <Film className="w-3.5 h-3.5" /> {busy.fal ? "Rendering…" : "Generate UGC"}
              </button>
            </div>
            <div className="text-[10px] text-slate-500 font-mono">Selfie-style · 9:16 · billed by fal.ai</div>
          </div>
        </div>
      </div>

      {videoJob && (
        <div
          data-testid="video-job-progress"
          className="rounded-md border border-blue-200 bg-blue-50 text-blue-800 px-3 py-2 text-xs flex items-center gap-2"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="font-mono uppercase tracking-wider text-[10px]">
            {videoJob.tier === "sora" ? "Sora 2" : "UGC"} · {videoJob.status}
          </span>
          <span className="text-[10px] font-mono text-blue-600">{videoJob.elapsed_sec}s elapsed</span>
          <span className="ml-auto text-[10px] font-mono text-blue-500">job {videoJob.job_id?.slice(-6)}</span>
        </div>
      )}

      {videoUrl && (
        <div data-testid="video-preview" className="rounded-md overflow-hidden border border-slate-200">
          <div className="flex items-center justify-between px-2 py-1 bg-slate-50 border-b border-slate-200">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-600">
              {videoTier === "sora" ? "Sora 2 · 9:16" :
               videoTier === "fal_ugc" ? "UGC · 9:16" :
               videoTier === "upload" ? "Uploaded · source" :
               "Slideshow · 1:1"}
            </span>
            <a href={`${BACKEND}${videoUrl}`} download data-testid="video-download" className="text-[10px] font-mono text-blue-700 hover:underline">
              download mp4 ↓
            </a>
          </div>
          <video
            controls
            src={`${BACKEND}${videoUrl}`}
            className={`w-full bg-black ${videoTier === "slideshow" ? "aspect-square" : "aspect-[9/16] max-h-[520px] mx-auto"}`}
          />

          {/* Remix controls (caption + button) */}
          <div className="p-2 border-t border-slate-200 space-y-1.5 bg-white" data-testid="remix-controls">
            <div className="flex items-center gap-1.5 text-[11px]">
              <Type className="w-3 h-3 text-slate-500" />
              <input
                value={overlayText}
                onChange={(e) => setOverlayText(e.target.value)}
                placeholder={autoCaption ? "Leave blank → AI writes the hook" : "Type your hook overlay…"}
                maxLength={80}
                data-testid="remix-overlay-input"
                className="flex-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              />
              <label className="flex items-center gap-1 text-[10px] font-mono text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoCaption}
                  onChange={(e) => setAutoCaption(e.target.checked)}
                  data-testid="remix-auto-caption"
                />
                AI hook
              </label>
            </div>
            <button
              onClick={remix}
              disabled={busy.remix}
              data-testid="remix-video-btn"
              className="w-full flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-1.5 text-xs font-medium disabled:opacity-60"
              title="Produce 9:16 / 4:5 / 1:1 crops, with optional burned-in hook overlay"
            >
              <Crop className="w-3.5 h-3.5" />
              {busy.remix ? "Remixing…" : "Auto-remix to 3 sizes"}
            </button>
          </div>

          {/* Tier 4 — ElevenLabs voiceover */}
          <div className="p-2 border-t border-slate-200 space-y-1.5 bg-white" data-testid="voiceover-controls">
            <div className="flex items-center gap-1.5 text-[11px]">
              <Mic className="w-3 h-3 text-slate-500" />
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                data-testid="vo-voice"
                className="bg-white border border-slate-200 rounded px-1.5 py-1 text-xs font-mono"
              >
                {voices.length === 0 && <option value="sarah">Sarah (default)</option>}
                {voices.map((v) => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-[10px] font-mono text-slate-500 cursor-pointer ml-auto">
                <input
                  type="checkbox"
                  checked={voMix}
                  onChange={(e) => setVoMix(e.target.checked)}
                  data-testid="vo-mix"
                />
                keep original audio (15%)
              </label>
            </div>
            <textarea
              value={voScript}
              onChange={(e) => setVoScript(e.target.value)}
              placeholder="Script to narrate (max 400 chars)…"
              maxLength={400}
              rows={2}
              data-testid="vo-script"
              className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-fuchsia-500/40 resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-mono text-slate-400">{voScript.length}/400</span>
              <button
                onClick={addVoiceover}
                disabled={busy.vo || !voScript.trim()}
                data-testid="add-voiceover-btn"
                className="flex items-center justify-center gap-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded px-2.5 py-1 text-xs font-medium disabled:opacity-60"
              >
                <Mic className="w-3 h-3" />
                {busy.vo ? "Synthesizing…" : "Add voiceover (Tier 4)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {remixUrls && (
        <div data-testid="video-remix-grid" className="grid grid-cols-3 gap-2">
          {[
            { key: "reels",  label: "Reels · 9:16", ratio: "aspect-[9/16]" },
            { key: "feed",   label: "Feed · 4:5",   ratio: "aspect-[4/5]" },
            { key: "square", label: "Square · 1:1", ratio: "aspect-square" },
          ].map((v) =>
            remixUrls[v.key] ? (
              <div key={v.key} className="rounded border border-slate-200 overflow-hidden" data-testid={`remix-${v.key}`}>
                <div className="flex items-center justify-between px-1.5 py-1 bg-slate-50 border-b border-slate-200">
                  <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">{v.label}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => addVoiceoverToVariant(v.key)}
                      disabled={busy[`vo_${v.key}`] || !voScript.trim()}
                      data-testid={`remix-vo-${v.key}`}
                      title={voScript.trim() ? `Stack voiceover (${voice}) on this variant` : "Type script in Tier-4 panel first"}
                      className="flex items-center gap-0.5 text-[9px] font-mono text-fuchsia-700 hover:text-white hover:bg-fuchsia-600 rounded px-1 py-0.5 border border-fuchsia-200 hover:border-fuchsia-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fuchsia-700"
                    >
                      <Mic className="w-2.5 h-2.5" />
                      {busy[`vo_${v.key}`] ? "…" : "vo"}
                    </button>
                    <a href={`${BACKEND}${remixUrls[v.key]}`} download className="text-[9px] font-mono text-blue-700 hover:underline">↓</a>
                  </div>
                </div>
                <video src={`${BACKEND}${remixUrls[v.key]}`} muted loop playsInline className={`w-full bg-black ${v.ratio}`} onMouseEnter={(e) => e.currentTarget.play()} onMouseLeave={(e) => e.currentTarget.pause()} />
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Slideshow button (kept in panel — uses images) */}
      <button
        onClick={buildSlideshow}
        disabled={busy.slide || images.length === 0}
        data-testid="build-video-btn"
        className="w-full flex items-center justify-center gap-1 bg-white border border-slate-200 hover:bg-slate-50 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60"
        title="Tier 2 — stitches your static creatives into a 1:1 slideshow"
      >
        <Film className="w-3.5 h-3.5" /> {busy.slide ? "Rendering slideshow…" : "Slideshow from images"}
      </button>
    </>
  );
}
